import {
  Box,
  Button,
  Grid,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import React, { useEffect, useState } from "react";
import PropertyCard from "./PropertyCard";
import { PropertiesObj } from "../../model";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PropertyOverviewMap from "./PropertyOverviewMap";
import ListIcon from "@mui/icons-material/List";
import LoadingAnimation from "../common/LoadingAnimation";
import { t } from "i18next";
import CollectionCard from "./CollectionCard";

interface PropertyOverviewListingProps {
  propertyCount: any;
  properties: any;
  loadingProperties: any;
  tempProps: any;
  propertiesPerPage: number;
  setPageIndex: any;
  propertyFilter: any;
  showMap: boolean;
  setShowMap: any;
  currentTab: any;
  pageIndex: number;
  switchTabs: any;
}

const PropertyOverviewListing = ({
  propertyCount,
  properties,
  loadingProperties,
  tempProps,
  propertiesPerPage,
  showMap,
  setShowMap,
  setPageIndex,
  propertyFilter,
  currentTab,
  pageIndex,
  switchTabs,
}: PropertyOverviewListingProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const onShowMap = () => setShowMap(!showMap);
  const [amountOfPages, setAmountOfPages] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    setAmountOfPages(Math.ceil(propertyCount / propertiesPerPage));
  }, [properties, propertyCount, propertiesPerPage]);

  useEffect(() => {
    if (!loadingProperties && pageLoading) {
      setPageLoading(false);
    }
  }, [loadingProperties]);

  const handlePageChange = (e: any, selectedPage: number) => {
    setPageLoading(true);
    setPageIndex(selectedPage - 1);
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE
  };

  return (
    <Grid
      padding={2}
      sx={{
        background: "#fff",
        borderRadius: 4,
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
        marginLeft: 0,
      }}
      item
      container
      xs={12}
      spacing={1}
      display={"flex"}
      alignContent={"flex-start"}
    >
      <Grid
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isSmallScreen ? "column" : "row"}
        sx={{
          width: "100%",
        }}
        alignItems={"center"}
        item
        xs={12}
      >
        <Box
          display={"flex"}
          justifyContent={"flex-start"}
          alignItems={"center"}
          gap={1}
          flex={1}
          width={"100%"}
        >
          <ToggleButtonGroup
            fullWidth={isSmallScreen ? true : false}
            sx={{
              paddingBottom: isSmallScreen ? 1 : 0,
              borderRadius: "6px",
            }}
          >
            <ToggleButton
              sx={{
                border: "1px solid" + theme.palette.primary.main,
                height: "2rem",
                padding: "1.2rem 1rem",
                color:
                  currentTab.name === "properties"
                    ? "#fff"
                    : theme.palette.primary.main,
                bgcolor:
                  currentTab.name === "properties"
                    ? theme.palette.primary.main
                    : "#fff",
                "&:hover": {
                  bgcolor:
                    currentTab.name === "properties" ? "#432dd6" : "#f9f9f9",
                  color:
                    currentTab.name === "properties"
                      ? "#fff"
                      : theme.palette.primary.main,
                },
              }}
              value={currentTab}
              onChange={() => switchTabs("properties")}
            >
              {t("overview.tab_properties")}
            </ToggleButton>
            <ToggleButton
              sx={{
                border: "1px solid" + theme.palette.primary.main,
                height: "2rem",
                padding: "1.2rem 1rem",
                color:
                  currentTab.name === "collections"
                    ? "#fff"
                    : theme.palette.primary.main,
                bgcolor:
                  currentTab.name === "collections"
                    ? theme.palette.primary.main
                    : "#fff",
                "&:hover": {
                  bgcolor:
                    currentTab.name === "collections" ? "#432dd6" : "#f9f9f9",
                  color:
                    currentTab.name === "collections"
                      ? "#fff"
                      : theme.palette.primary.main,
                },
              }}
              value={currentTab}
              onChange={() => switchTabs("collections")}
            >
              {t("overview.tab_collections")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box
          display={"flex"}
          flex={1}
          justifyContent={"flex-end"}
          width={"100%"}
        >
          <Button
            fullWidth={isSmallScreen ? true : false}
            onClick={onShowMap}
            startIcon={!showMap ? <LocationOnOutlinedIcon /> : <ListIcon />}
            variant="outlined"
          >
            {!showMap ? t("overview.map") : t("overview.list")}
          </Button>
        </Box>
      </Grid>
      <Box
        display={"flex"}
        sx={{
          marginTop: "2rem",
          marginLeft: ".6rem",
        }}
      >
        <HomeWorkOutlinedIcon color="primary" />
        <Typography variant={"body1"} fontWeight={"bold"} marginLeft=".5rem">
          {propertyCount}{" "}
          {propertyCount !== 1 ? t("overview.results") : t("overview.result")}
        </Typography>
      </Box>
      {showMap ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
          }}
          padding={isSmallScreen ? 0 : 1}
          paddingTop={2}
        >
          <PropertyOverviewMap
            propertyFilter={propertyFilter}
            mapTypeName={currentTab.name}
          />
        </Box>
      ) : (
        <>
          <Grid container item padding={2} xs={12}>
            {loadingProperties || pageLoading ? (
              <Box width={"100%"} height={700}>
                <LoadingAnimation />
              </Box>
            ) : (
              properties?.length > 0 &&
              properties.map((property: PropertiesObj, index: number) => (
                <Grid key={property.propertyId} item xs={12}>
                  {property.propertyCollectionDetails !== null ? (
                    <CollectionCard
                      property={property}
                      loading={false}
                      index={index}
                    />
                  ) : (
                    <PropertyCard
                      property={property}
                      loading={false}
                      index={index}
                    />
                  )}
                </Grid>
              ))
            )}
          </Grid>
          <Box
            sx={{
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Pagination
              count={amountOfPages}
              page={pageIndex + 1}
              variant="outlined"
              shape="rounded"
              onChange={handlePageChange}
            />
          </Box>
        </>
      )}
    </Grid>
  );
};

export default PropertyOverviewListing;
